/*
 * RantanOfertas | RO Dependent Variants for Venue | v1.1
 * Filtra las combinaciones disponibles y mantiene el filtro cuando Venue
 * reconstruye los <option> despues de un cambio de variante.
 *
 * Requiere data-ro-dependent-variants en el picker del producto.
 * Compatible con 2 selectores nativos <select class="single-option-selector">.
 * No modifica los datos de variantes ni los inputs de compra de Shopify.
 */
(() => {
  'use strict';

  const PICKER =
    'variant-selects[data-ro-dependent-variants], ' +
    'variant-mixed-inputs[data-ro-dependent-variants]';

  const states = new WeakMap();
  const pendingNotifications = new WeakMap();

  function readState(root) {
    const selects = Array.from(root.querySelectorAll('select.single-option-selector'));
    if (selects.length !== 2) return null;

    const json = root.querySelector('script[id^="all-variant-data-"]');
    if (!json) return null;

    const source = json.textContent.trim();
    const previous = states.get(root);
    if (
      previous && previous.source === source &&
      previous.selects[0] === selects[0] &&
      previous.selects[1] === selects[1]
    ) {
      // Venue puede haber reinsertado opciones, sin reemplazar los <select>.
      rememberOptions(selects[0], previous.catalogs[0]);
      rememberOptions(selects[1], previous.catalogs[1]);
      return previous;
    }

    let variants;
    try {
      variants = JSON.parse(source);
    } catch (error) {
      console.warn('[RO Variants] No se pudo leer el JSON del producto.', error);
      return null;
    }

    if (!Array.isArray(variants)) return null;

    const available = variants.filter(
      (v) => v.available === true &&
        Array.isArray(v.options) && v.options.length >= 2
    );
    if (!available.length) return null; // Sin opciones disponibles: respeta Venue.

    // No intervenir con Combined Listings (valores vinculados a otro producto).
    if (selects.some((select) => Array.from(select.options)
      .some((option) => Boolean(option.dataset.productUrl)))) return null;

    const reuseCatalogs = previous && previous.source === source;
    const catalogs = reuseCatalogs
      ? previous.catalogs
      : [new Map(), new Map()];

    rememberOptions(selects[0], catalogs[0]);
    rememberOptions(selects[1], catalogs[1]);

    const state = { source, selects, available, catalogs };
    states.set(root, state);
    return state;
  }

  function rememberOptions(select, catalog) {
    for (const option of select.options) {
      if (catalog.has(option.value)) continue;
      const template = option.cloneNode(true);
      // Venue presenta "- No disponible" en valores globales sin stock
      // para la primera combinacion. Guardar siempre la etiqueta limpia.
      const label = option.dataset.baseText || option.value;
      template.textContent = label;
      template.disabled = false;
      template.selected = false;
      catalog.set(option.value, { template, label });
    }
  }

  function filterSelect(select, catalog, allowedValues) {
    const wanted = Array.from(catalog.entries())
      .filter(([value]) => allowedValues.has(value));
    if (!wanted.length) return { selectionChanged: false, listChanged: false };

    const previousValue = select.value;
    const nextValue = allowedValues.has(previousValue)
      ? previousValue
      : wanted[0][0];

    const current = Array.from(select.options);
    const sameList = current.length === wanted.length && wanted.every(
      ([value, item], i) => current[i].value === value &&
        current[i].textContent === item.label && !current[i].disabled
    );

    if (!sameList) {
      const options = wanted.map(([, item]) => item.template.cloneNode(true));
      select.replaceChildren(...options);
    }

    if (select.value !== nextValue) select.value = nextValue;

    return {
      selectionChanged: previousValue !== select.value,
      listChanged: !sameList
    };
  }

  function synchronize(root) {
    const state = readState(root);
    if (!state) return null;

    const [first, second] = state.selects;
    const firstValues = new Set(state.available.map((v) => v.options[0]));
    const firstResult = filterSelect(first, state.catalogs[0], firstValues);

    const secondValues = new Set(
      state.available
        .filter((v) => v.options[0] === first.value)
        .map((v) => v.options[1])
    );
    const secondResult = filterSelect(second, state.catalogs[1], secondValues);

    return { state, firstResult, secondResult };
  }

  function informVenue(root, targetIndex) {
    if (pendingNotifications.has(root)) return;
    pendingNotifications.set(root, targetIndex);

    // Notificar despues de las mutaciones pendientes, nunca desde el observer
    // de forma sincrona. Venue sigue siendo responsable del precio, ID y carrito.
    requestAnimationFrame(() => {
      const index = pendingNotifications.get(root);
      pendingNotifications.delete(root);
      if (!root.isConnected) return;

      const state = readState(root);
      if (!state) return;

      const target = state.selects[index];
      if (target) target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function apply(root, notifyNative = false) {
    if (!root || !root.isConnected || !root.matches(PICKER)) return;
    const result = synchronize(root);
    if (!result || !notifyNative) return;

    if (result.firstResult.selectionChanged) {
      informVenue(root, 0);
    } else if (result.secondResult.selectionChanged) {
      informVenue(root, 1);
    }
  }

  // Captura ANTES de que Venue lea los valores de los selects.
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('select.single-option-selector')) return;
    const root = event.target.closest(PICKER);
    if (!root) return;

    // El evento original del cliente llegara a Venue con la combinacion valida.
    apply(root, false);
  }, true);

  function scan(scope = document) {
    if (scope instanceof Element && scope.matches(PICKER)) apply(scope, true);
    scope.querySelectorAll(PICKER).forEach((root) => apply(root, true));
  }

  function handleMutations(records) {
    const affected = new Set();

    for (const record of records) {
      const el = record.target instanceof Element
        ? record.target
        : record.target.parentElement;
      const root = el && el.closest(PICKER);
      if (root) affected.add(root);

      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(PICKER)) affected.add(node);
        node.querySelectorAll(PICKER).forEach((picker) => affected.add(picker));
      }
    }

    // Las mutaciones del propio filtro pasan otra vez por aqui, pero como
    // synchronize() es idempotente no generan un ciclo infinito.
    affected.forEach((root) => apply(root, true));
  }

  function start() {
    const observer = new MutationObserver(handleMutations);
    observer.observe(document.documentElement, {
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
      subtree: true
    });
    scan();
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
